import { Injectable, signal, computed, inject } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { ApiService } from './api.service';
import {
  SendOtpRequest, SendOtpResponse,
  ResendOtpRequest, ResendOtpResponse,
  VerifyOtpRequest, VerifyOtpResponse,
} from '../models/auth.model';

export interface AuthState {
  phone:           string;
  token:           string;
  isVerified:      boolean;
  profileComplete: boolean;
  displayName:     string;
  username:        string;
  email:           string;
  bio:             string;
  categoryIds:     string[];
  avatarUrl:       string;
  avatarFile?:     File;
}

const INIT: AuthState = {
  phone: '', token: '', isVerified: false, profileComplete: false,
  displayName: '', username: '', email: '', bio: '', categoryIds: [], avatarUrl: '',
};

const STATE_KEY   = 'creatorAuthState';
const TOKEN_KEY   = 'token';
const REFRESH_KEY = 'refreshToken';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private api = inject(ApiService);

  private _s = signal<AuthState>(this.loadState());

  readonly state           = this._s.asReadonly();
  readonly phone           = computed(() => this._s().phone);
  readonly isVerified      = computed(() => this._s().isVerified);
  readonly profileComplete = computed(() => this._s().profileComplete);

  // Transient (not persisted) — the actual resend cooldown the server just set, from whichever
  // of send/resend last ran. verify-otp.component's countdown reads this instead of a hardcoded
  // guess, so the two can't drift apart (they previously did: 30s client-side vs 60s server-side).
  private _cooldownRemaining = signal(0);
  readonly cooldownRemaining = this._cooldownRemaining.asReadonly();

  // Dev-only: no SMS provider wired up yet, so the backend echoes the OTP it just
  // generated back in the send/resend response. Shown on-screen so testers don't
  // need server log access — set to null once verified or on a fresh phone submit.
  private _devOtp = signal<string | null>(null);
  readonly devOtp = this._devOtp.asReadonly();

  // ── Creator auth API (trendly-api /api/v1/creator/auth) ────────────

  sendOtp(phone: string): Observable<SendOtpResponse> {
    return this.api
      .post<SendOtpResponse>('/auth/send-otp', { phone } satisfies SendOtpRequest)
      .pipe(tap(res => {
        this.setPhone(phone);
        this._cooldownRemaining.set(res.cooldownRemaining);
        this._devOtp.set(res.otp != null ? String(res.otp) : null);
      }));
  }

  resendOtp(phone: string): Observable<ResendOtpResponse> {
    return this.api
      .post<ResendOtpResponse>('/auth/resend-otp', { phone } satisfies ResendOtpRequest)
      .pipe(tap(res => {
        this._cooldownRemaining.set(res.cooldownRemaining);
        this._devOtp.set(res.otp != null ? String(res.otp) : null);
      }));
  }

  verifyOtp(phone: string, otp: string): Observable<VerifyOtpResponse> {
    return this.api
      .post<VerifyOtpResponse>('/auth/verify-otp', { phone, otp } satisfies VerifyOtpRequest)
      .pipe(tap(res => {
        this.setVerified(res.accessToken, res.isSignupComplete, res.refreshToken);
        this._devOtp.set(null);
      }));
  }

  logout(): void {
    if (this.isLoggedIn()) {
      // Best-effort — invalidates the refresh token server-side. Don't block sign-out on it.
      this.api.post<null>('/auth/logout', {}).subscribe({ error: () => {} });
    }
    this.reset();
  }

  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_KEY);
  }

  isLoggedIn(): boolean {
    return !!this.getToken();
  }

  // ── Local state mutations ───────────────────────────────────────────

  setPhone(phone: string) {
    this._s.update(s => ({ ...s, phone }));
    this.persist();
  }

  setVerified(token: string, profileComplete: boolean, refreshToken?: string) {
    this._s.update(s => ({ ...s, token, isVerified: true, profileComplete }));
    localStorage.setItem(TOKEN_KEY, token);
    if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
    this.persist();
  }

  setDetails(d: { displayName: string; username: string; email: string; bio: string }) {
    this._s.update(s => ({ ...s, ...d }));
    this.persist();
  }

  setCategoryIds(ids: string[]) {
    this._s.update(s => ({ ...s, categoryIds: ids }));
    this.persist();
  }

  setAvatar(url: string, file?: File) {
    this._s.update(s => ({ ...s, avatarUrl: url, avatarFile: file }));
    this.persist();
  }

  setProfileComplete(v: boolean) {
    this._s.update(s => ({ ...s, profileComplete: v }));
    this.persist();
  }

  buildProfilePayload() {
    const s = this._s();
    return {
      displayName: s.displayName,
      username:    s.username,
      email:       s.email,
      bio:         s.bio,
      categoryIds: s.categoryIds,
      avatarUrl:   s.avatarUrl,
    };
  }

  reset() {
    this._s.set({ ...INIT });
    localStorage.removeItem(STATE_KEY);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
  }

  // ── Persistence (survives a hard refresh — see localStorage keys above) ──

  private persist() {
    const { avatarFile, ...serializable } = this._s();
    localStorage.setItem(STATE_KEY, JSON.stringify(serializable));
  }

  private loadState(): AuthState {
    try {
      const raw = localStorage.getItem(STATE_KEY);
      return raw ? { ...INIT, ...JSON.parse(raw) } : { ...INIT };
    } catch {
      return { ...INIT };
    }
  }
}
